/**
 * offlineAuth — local credential cache so users can log in without internet.
 *
 * SECURITY MODEL (read this before changing anything):
 *
 *   We store a PBKDF2-derived hash (NOT the plaintext password) in
 *   localStorage. The salt is per-device (random UUID) + per-email so the
 *   same password on two devices produces different hashes.
 *
 *   Threat model:
 *     - Casual user with browser DevTools: cannot recover password from hash
 *       in any reasonable time (PBKDF2-SHA256 × 100,000 iterations).
 *     - Stolen device with disk access: same protection — they get the hash,
 *       not the password.
 *     - Online attack on the cloud: unaffected (this is local only).
 *
 *   What we don't protect against:
 *     - A determined attacker brute-forcing weak passwords offline.
 *       Mitigation: enforce strong passwords at signup.
 *     - Malicious browser extension. (Same risk as any web app.)
 *
 *   For POS counter use: the device is physically supervised, the trade-off
 *   is appropriate. For personal-device installs you would NOT want this on.
 *
 * USAGE:
 *
 *   On successful online login → cacheOfflineCredentials(email, password, contextSnapshot)
 *   On offline login attempt   → verifyOfflineCredentials(email, password)
 *                               → null if no match, or the saved snapshot if match
 *   On logout                  → caller decides whether to clearOfflineCredentials(email)
 *                                (default: keep — so user can offline-login again)
 *
 * The "contextSnapshot" is whatever the caller needs to restore AuthContext
 * state without hitting Firestore: userProfile, activeCompany, companyUser,
 * financialYear, branches, etc.
 */

const STORAGE_KEY = 'amal.offlineAuth.v1';
const DEVICE_SALT_KEY = 'amal.offlineAuth.deviceSalt';
const PBKDF2_ITERATIONS = 100000;

// ─── Device salt (random per browser install) ───────────────────────────

const getDeviceSalt = () => {
  try {
    let salt = localStorage.getItem(DEVICE_SALT_KEY);
    if (!salt) {
      salt = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      localStorage.setItem(DEVICE_SALT_KEY, salt);
    }
    return salt;
  } catch {
    return 'fallback-device-salt';
  }
};

// ─── Hash derivation ────────────────────────────────────────────────────

const bufToHex = (buf) =>
  Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');

const deriveHash = async (password, saltString) => {
  if (!crypto?.subtle) {
    throw new Error('Web Crypto API not available — offline auth requires HTTPS');
  }
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: enc.encode(saltString),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256'
    },
    keyMaterial,
    256
  );
  return bufToHex(bits);
};

// ─── Storage I/O ────────────────────────────────────────────────────────

const readAll = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeAll = (all) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (err) {
    console.warn('[offlineAuth] storage write failed:', err);
  }
};

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

// ─── Public API ─────────────────────────────────────────────────────────

/**
 * Save a credential hash + restorable context so the user can offline-login later.
 * Call this from your successful online-login path.
 *
 * @param {string} email
 * @param {string} password - never stored as plaintext; only the derived hash is kept
 * @param {object} contextSnapshot - data needed to restore AuthContext offline
 *                                   (userProfile, activeCompany, companyUser,
 *                                   financialYear, branches, etc.)
 */
export const cacheOfflineCredentials = async (email, password, contextSnapshot) => {
  const key = normalizeEmail(email);
  if (!key || !password) return;

  const deviceSalt = getDeviceSalt();
  const fullSalt = `${deviceSalt}:${key}`;
  const hashHex = await deriveHash(password, fullSalt);

  const all = readAll();
  all[key] = {
    hashHex,
    saltHex: deviceSalt, // for diagnostic; full salt re-derived on verify
    iterations: PBKDF2_ITERATIONS,
    contextSnapshot,
    cachedAt: new Date().toISOString()
  };
  writeAll(all);
};

/**
 * Verify an offline login attempt.
 *
 * @returns {Promise<{ snapshot: object } | { error: string }>} On success,
 *   the cached contextSnapshot. On failure, a structured error reason
 *   (so callers can show a specific message to the user).
 */
export const verifyOfflineCredentialsDetailed = async (email, password) => {
  const key = normalizeEmail(email);
  if (!key) return { error: 'empty-email' };
  if (!password) return { error: 'empty-password' };

  const all = readAll();
  const entry = all[key];

  // Diagnostic visible in the console to help cashiers / support figure out
  // why a specific device can't offline-login.
  console.log('[offlineAuth] verify attempt', {
    email: key,
    haveCachedEntry: !!entry,
    cachedEmails: Object.keys(all),
    deviceSaltPresent: !!localStorage.getItem(DEVICE_SALT_KEY)
  });

  if (!entry) return { error: 'no-cached-credentials-for-email' };

  const deviceSalt = getDeviceSalt();
  const fullSalt = `${deviceSalt}:${key}`;
  let attemptHash;
  try {
    attemptHash = await deriveHash(password, fullSalt);
  } catch (e) {
    console.warn('[offlineAuth] hash derivation failed:', e);
    return { error: 'hash-derivation-failed', detail: String(e?.message || e) };
  }

  if (attemptHash !== entry.hashHex) {
    console.log('[offlineAuth] hash mismatch — wrong password', {
      attemptHashPrefix: attemptHash.slice(0, 8),
      cachedHashPrefix: entry.hashHex.slice(0, 8)
    });
    return { error: 'wrong-password' };
  }

  return { snapshot: entry.contextSnapshot };
};

/**
 * Update only the contextSnapshot for an already-cached email — without
 * re-deriving the hash. Useful when the user switches branch / company,
 * or when state refreshes from Firestore, and we want offline-login to land
 * them in the same place next time.
 */
export const updateCachedContext = (email, contextSnapshot) => {
  const key = normalizeEmail(email);
  if (!key) return;
  const all = readAll();
  if (!all[key]) return; // never cached — nothing to update
  all[key].contextSnapshot = contextSnapshot;
  all[key].contextUpdatedAt = new Date().toISOString();
  writeAll(all);
};

/**
 * Remove cached credentials for one email (typical use: explicit user
 * action to "forget this device").
 */
export const clearOfflineCredentials = (email) => {
  const key = normalizeEmail(email);
  if (!key) return;
  const all = readAll();
  delete all[key];
  writeAll(all);
};

